import { Link, router, usePage } from "@inertiajs/react";
import { Clock, Plus, SearchX } from "lucide-react";
import { useCallback, useState } from "react";
import { AccountCard } from "@/bizminer/App/Accounts/_components/AccountCard.tsx";
import { AccountsPagination } from "@/bizminer/App/Accounts/_components/AccountsPagination.tsx";
import { AccountsTable } from "@/bizminer/App/Accounts/_components/AccountsTable.tsx";
import { FilterDropdown } from "@/bizminer/App/Accounts/_components/FilterDropdown.tsx";
import { IndustryFilter } from "@/bizminer/App/Accounts/_components/IndustryFilter.tsx";
import { LISTING_STATUS_LABELS } from "@/bizminer/App/Accounts/_components/listingStatus.ts";
import { PendingRequestsOverview } from "@/bizminer/App/Accounts/_components/PendingRequestsOverview.tsx";
import { ViewToggle } from "@/bizminer/App/Accounts/_components/ViewToggle.tsx";
import BizminerLayout from "@/bizminer/layouts/app/BizminerLayout.tsx";
import { SearchInput } from "@/components/SearchInput";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert.tsx";
import { Button } from "@/components/ui/button.tsx";
import type { GicsTreeNode, SelectedIndustry } from "@/lib/industry";
import {
  bizminer_app_accounts_path,
  new_bizminer_app_account_path,
} from "@/routes";
import type { PagyMetadata } from "@/types";
import type { BizminerInertiaSharedProps } from "@/types/inertia.ts";
import type {
  BizminerAccount,
  BizminerInsightRequestDashboard,
  BizminerOrganizationDisplay,
  BizminerStrategicPillarOption,
} from "@/types/serializers";

interface Props {
  organization: BizminerOrganizationDisplay;
  accounts: BizminerAccount[];
  pending_insight_requests: BizminerInsightRequestDashboard[];
  user_pending: boolean;
  pagy: PagyMetadata;
  strategic_pillars: BizminerStrategicPillarOption[];
  initial_industry: SelectedIndustry | null;
  gics_tree: GicsTreeNode[];
}

function useIndustryFilter(
  initialIndustry: SelectedIndustry | null,
  onNavigate: (params: URLSearchParams) => void,
) {
  const { url } = usePage();
  const searchParams = new URLSearchParams(url.split("?")[1] || "");
  const [selectedIndustry, setSelectedIndustry] =
    useState<SelectedIndustry | null>(initialIndustry);

  const handleChange = useCallback(
    (industry: SelectedIndustry | null) => {
      setSelectedIndustry(industry);
      const params = new URLSearchParams(searchParams);
      params.delete("page");
      if (industry) {
        params.set("industry", industry.id);
      } else {
        params.delete("industry");
      }
      onNavigate(params);
    },
    [searchParams, onNavigate],
  );

  return { selectedIndustry, handleChange };
}

export default function Index({
  accounts,
  pending_insight_requests,
  user_pending,
  pagy,
  strategic_pillars,
  initial_industry,
  gics_tree,
}: Props) {
  const [view, setView] = useState<"cards" | "table">("cards");
  const { url } = usePage();
  const searchParams = new URLSearchParams(url.split("?")[1] || "");
  const searchValue = searchParams.get("search") || "";

  const {
    props: { permissions },
  } = usePage<BizminerInertiaSharedProps>();

  const handleNavigate = useCallback((params: URLSearchParams) => {
    const queryString = params.toString();
    router.get(
      `${bizminer_app_accounts_path()}${queryString ? `?${queryString}` : ""}`,
      {},
      { preserveState: true, preserveScroll: true },
    );
  }, []);

  const { selectedIndustry, handleChange: handleIndustryChange } =
    useIndustryFilter(initial_industry, handleNavigate);

  return (
    <BizminerLayout>
      <div className="space-y-8">
        {user_pending && (
          <Alert variant="warning">
            <Clock className="h-4 w-4" />
            <AlertTitle>Account Pending Approval</AlertTitle>
            <AlertDescription>
              Your account is awaiting approval from an administrator. You'll
              have full access once your account is activated.
            </AlertDescription>
          </Alert>
        )}
        <div>
          <h1 className="text-2xl font-bold">Accounts</h1>
        </div>
        <div>
          <div className="flex items-center gap-3 mb-4">
            <SearchInput
              initialValue={searchValue}
              placeholder="Search accounts..."
            />
            <FilterDropdown
              label="Pillars"
              paramName="pillar"
              options={strategic_pillars.map((p) => ({
                value: p.slug,
                label: p.title,
              }))}
            />
            <FilterDropdown
              label="Listing"
              paramName="listing_status"
              allLabel="All Listing Statuses"
              options={[
                { value: "listed", label: LISTING_STATUS_LABELS.listed },
                { value: "unlisted", label: LISTING_STATUS_LABELS.unlisted },
              ]}
            />
            <IndustryFilter
              value={selectedIndustry}
              onChange={handleIndustryChange}
              treeData={gics_tree}
            />
            <ViewToggle view={view} onViewChange={setView} />

            {permissions.can_manage_accounts && (
              <Link href={new_bizminer_app_account_path()}>
                <Button>
                  <Plus className="h-4 w-4 mr-1" />
                  New Account
                </Button>
              </Link>
            )}
          </div>

          {accounts.length === 0 ? (
            <div className="text-center py-16">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted mb-4">
                <SearchX className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-1">No accounts found</h3>
              <p className="text-sm text-muted-foreground">
                {searchValue || selectedIndustry
                  ? "Try adjusting your filters to find what you're looking for."
                  : "Create your first account to get started."}
              </p>
            </div>
          ) : view === "cards" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pb-5">
              {accounts.map((account) => (
                <AccountCard key={account.id} account={account} />
              ))}
            </div>
          ) : (
            <AccountsTable accounts={accounts} />
          )}

          {accounts.length > 0 && <AccountsPagination pagy={pagy} />}
        </div>
        <PendingRequestsOverview pendingRequests={pending_insight_requests} />
      </div>
    </BizminerLayout>
  );
}
